import { useState, useCallback } from 'react'
import type {
  ElementMessage,
  ElementFolder
} from '../types'
import type { ElementMessageListQueryParams } from '../api'
import {
  fetchElementMessageList,
  fetchElementMessageById,
  fetchElementMessageBody,
  updateElementMessage,
  deleteElementMessage,
  bulkUpdateElementMessages,
  fetchElementFolderList
} from '../api'

export interface ElementMailState {
  messageList: ElementMessage[]
  activeMessage: ElementMessage | null
  activeMessageBodyContent: string | null
  folderList: ElementFolder[]
  activeFolder: string
  totalMessageCount: number
  currentPageNumber: number
  totalPageCount: number
  isMessagesLoading: boolean
  isMessageBodyLoading: boolean
  messageListError: string | null
  searchQueryString: string
}

export interface ElementMailActions {
  loadElementMessageList: (queryParams?: ElementMessageListQueryParams) => Promise<void>
  openElementMessage: (messageId: string) => Promise<void>
  markElementMessageRead: (messageId: string, isRead: boolean) => Promise<void>
  toggleElementMessageStar: (messageId: string, isStarred: boolean) => Promise<void>
  moveElementMessageToFolder: (messageId: string, targetFolder: string) => Promise<void>
  deleteElementMessageById: (messageId: string) => Promise<void>
  bulkActionOnElementMessages: (messageIds: string[], bulkAction: string, targetFolder?: string) => Promise<void>
  loadElementFolderList: () => Promise<void>
  setActiveElementFolder: (folderName: string) => void
  setElementSearchQuery: (searchQuery: string) => void
  clearActiveElementMessage: () => void
}

export type ElementMailStoreValue = ElementMailState & ElementMailActions

export function useElementMailStore(): ElementMailStoreValue {
  const [messageList, setMessageList] = useState<ElementMessage[]>([])
  const [activeMessage, setActiveMessage] = useState<ElementMessage | null>(null)
  const [activeMessageBodyContent, setActiveMessageBodyContent] = useState<string | null>(null)
  const [folderList, setFolderList] = useState<ElementFolder[]>([])
  const [activeFolder, setActiveFolder] = useState<string>('INBOX')
  const [totalMessageCount, setTotalMessageCount] = useState<number>(0)
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1)
  const [totalPageCount, setTotalPageCount] = useState<number>(1)
  const [isMessagesLoading, setIsMessagesLoading] = useState<boolean>(false)
  const [isMessageBodyLoading, setIsMessageBodyLoading] = useState<boolean>(false)
  const [messageListError, setMessageListError] = useState<string | null>(null)
  const [searchQueryString, setSearchQueryString] = useState<string>('')

  const loadElementMessageList = useCallback(async (
    queryParams: ElementMessageListQueryParams = {}
  ): Promise<void> => {
    setIsMessagesLoading(true)
    setMessageListError(null)
    try {
      const messageListResponse = await fetchElementMessageList({
        folder: activeFolder,
        page: currentPageNumber,
        ...queryParams
      })
      setMessageList(messageListResponse.messages)
      setTotalMessageCount(messageListResponse.totalMessages)
      setCurrentPageNumber(messageListResponse.currentPage)
      setTotalPageCount(messageListResponse.totalPages)
    } catch (messageLoadError: unknown) {
      const errorMessage = messageLoadError instanceof Error ? messageLoadError.message : 'Failed to load messages'
      setMessageListError(errorMessage)
    } finally {
      setIsMessagesLoading(false)
    }
  }, [activeFolder, currentPageNumber])

  const openElementMessage = useCallback(async (messageId: string): Promise<void> => {
    setIsMessageBodyLoading(true)
    try {
      const fullMessageRecord = await fetchElementMessageById(messageId)
      setActiveMessage(fullMessageRecord)
      const rawEmailBodyContent = await fetchElementMessageBody(messageId)
      setActiveMessageBodyContent(rawEmailBodyContent)
      setMessageList(previousMessageList =>
        previousMessageList.map(existingMessage =>
          existingMessage.id === messageId
            ? { ...existingMessage, is_read: 1 }
            : existingMessage
        )
      )
    } catch (messageOpenError: unknown) {
      const errorMessage = messageOpenError instanceof Error ? messageOpenError.message : 'Failed to open message'
      setMessageListError(errorMessage)
    } finally {
      setIsMessageBodyLoading(false)
    }
  }, [])

  const markElementMessageRead = useCallback(async (
    messageId: string,
    isRead: boolean
  ): Promise<void> => {
    await updateElementMessage(messageId, { isRead })
    setMessageList(previousMessageList =>
      previousMessageList.map(existingMessage =>
        existingMessage.id === messageId
          ? { ...existingMessage, is_read: isRead ? 1 : 0 }
          : existingMessage
      )
    )
  }, [])

  const toggleElementMessageStar = useCallback(async (
    messageId: string,
    isStarred: boolean
  ): Promise<void> => {
    await updateElementMessage(messageId, { isStarred })
    setMessageList(previousMessageList =>
      previousMessageList.map(existingMessage =>
        existingMessage.id === messageId
          ? { ...existingMessage, is_starred: isStarred ? 1 : 0 }
          : existingMessage
      )
    )
  }, [])

  const moveElementMessageToFolder = useCallback(async (
    messageId: string,
    targetFolder: string
  ): Promise<void> => {
    await updateElementMessage(messageId, { targetFolder })
    setMessageList(previousMessageList =>
      previousMessageList.filter(existingMessage => existingMessage.id !== messageId)
    )
  }, [])

  const deleteElementMessageById = useCallback(async (messageId: string): Promise<void> => {
    await deleteElementMessage(messageId)
    setMessageList(previousMessageList =>
      previousMessageList.filter(existingMessage => existingMessage.id !== messageId)
    )
    if (activeMessage?.id === messageId) {
      setActiveMessage(null)
      setActiveMessageBodyContent(null)
    }
  }, [activeMessage])

  const bulkActionOnElementMessages = useCallback(async (
    messageIds: string[],
    bulkAction: string,
    targetFolder?: string
  ): Promise<void> => {
    await bulkUpdateElementMessages(messageIds, bulkAction as import('../types').ElementBulkAction, targetFolder)
    await loadElementMessageList()
  }, [loadElementMessageList])

  const loadElementFolderList = useCallback(async (): Promise<void> => {
    const retrievedFolderList = await fetchElementFolderList()
    setFolderList(retrievedFolderList)
  }, [])

  const setActiveElementFolder = useCallback((folderName: string): void => {
    setActiveFolder(folderName)
    setActiveMessage(null)
    setActiveMessageBodyContent(null)
    setCurrentPageNumber(1)
  }, [])

  const setElementSearchQuery = useCallback((searchQuery: string): void => {
    setSearchQueryString(searchQuery)
  }, [])

  const clearActiveElementMessage = useCallback((): void => {
    setActiveMessage(null)
    setActiveMessageBodyContent(null)
  }, [])

  return {
    messageList,
    activeMessage,
    activeMessageBodyContent,
    folderList,
    activeFolder,
    totalMessageCount,
    currentPageNumber,
    totalPageCount,
    isMessagesLoading,
    isMessageBodyLoading,
    messageListError,
    searchQueryString,
    loadElementMessageList,
    openElementMessage,
    markElementMessageRead,
    toggleElementMessageStar,
    moveElementMessageToFolder,
    deleteElementMessageById,
    bulkActionOnElementMessages,
    loadElementFolderList,
    setActiveElementFolder,
    setElementSearchQuery,
    clearActiveElementMessage
  }
}
